import { Button, message, Modal, Switch, Table } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { useCallback, useMemo, useState } from 'react';
import { DraggableContainer, DragHandle, SortableItem } from 'components/DragTableComponent';
import { SortableContainerProps, SortEnd } from 'react-sortable-hoc';
import { TickTypeSourceItem } from 'hooks/business/chat/useTickTypeSource';
import { ColumnsType } from 'antd/lib/table';
import { UserDB } from 'lib/db';
import { useLatest } from 'ahooks';
import { useTranslate } from '@tolgee/react';
import { useTodoType } from 'views/Aide/Basis/Todo/TodoType';
import { useQueryClient } from '@tanstack/react-query';
import { queries } from 'services/queries';

interface TicketSettingProps {
  storeId: string;
  taskId: number;
  typeSource: TickTypeSourceItem[];
  onRefresh?: () => void;
}

const ModalContent = (props: TicketSettingProps) => {
  const { storeId, taskId, typeSource, onRefresh } = props;
  const { t } = useTranslate();
  const queryClient = useQueryClient();

  const existNames = useMemo(() => typeSource.map((e) => e.typeLabel), [typeSource]);

  const [instance, open] = useTodoType({
    taskId,
    refresh: () => {
      queryClient.invalidateQueries({ queryKey: queries.tickets.task(storeId).queryKey });
    },
    existNames,
  });

  const changeLocalEnable = useCallback(
    async (type: Todo.Type, localEnable: boolean, sortIndex?: number) => {
      await UserDB.instance?.todoTypeSource.put({
        taskId,
        type,
        localEnable,
        sortIndex,
      });
      onRefresh?.();
    },
    [taskId, onRefresh],
  );

  const columns: ColumnsType<TickTypeSourceItem> = [
    {
      title: '',
      dataIndex: 'sort',
      width: 50,
      className: 'drag-visible',
      render: () => {
        return <DragHandle />;
      },
    },
    {
      title: t('todo.待办类型'),
      dataIndex: 'typeLabel',
      key: 'typeLabel',
    },
    {
      title: t('account.enable'),
      dataIndex: 'content',
      render: (_, record) => {
        return (
          <Switch
            checked={record.localEnable}
            onChange={(checked) => {
              changeLocalEnable(record.type, checked, record.sortIndex);
            }}
          />
        );
      },
    },
  ];

  const typeSourceRef = useLatest(typeSource);
  const onSortEnd = useCallback(
    async ({ oldIndex, newIndex }: SortEnd) => {
      if (oldIndex === newIndex) return;
      const currentTypeSource = typeSourceRef.current;
      const current = currentTypeSource[oldIndex];
      if (!current) return;

      const getSortValue = (item: TickTypeSourceItem | undefined, fallback: number) => {
        return item?.sortIndex ?? fallback;
      };

      let nextSortIndex = getSortValue(current, oldIndex);
      if (newIndex === 0) {
        nextSortIndex = getSortValue(currentTypeSource[0], 0) - 1;
      } else if (newIndex === currentTypeSource.length - 1) {
        nextSortIndex =
          getSortValue(
            currentTypeSource[currentTypeSource.length - 1],
            currentTypeSource.length - 1,
          ) + 1;
      } else {
        const prev =
          oldIndex > newIndex ? currentTypeSource[newIndex - 1] : currentTypeSource[newIndex + 1];
        const next = currentTypeSource[newIndex];
        nextSortIndex = (getSortValue(prev, newIndex - 1) + getSortValue(next, newIndex)) / 2;
      }

      try {
        await UserDB.instance?.todoTypeSource.put({
          taskId,
          type: current.type,
          localEnable: current.localEnable,
          sortIndex: nextSortIndex,
        });
        onRefresh?.();
      } catch (_e) {
        message.error(t('translation.操作失败，请稍后再试'));
      }
    },
    [typeSourceRef, taskId, onRefresh, t],
  );

  const Container = (props: SortableContainerProps) => (
    <DraggableContainer onSortEnd={onSortEnd} {...props} />
  );

  const DraggableBodyRow: React.FC<any> = ({ className, style, ...restProps }) => {
    // function findIndex base on Table rowKey props and should always be a right array index
    const index = typeSource?.findIndex((x) => x.type === restProps['data-row-key']);
    return <SortableItem index={index} {...restProps} />;
  };

  return (
    <>
      <div className="flex justify-end mb-3">
        <Button type="link" size="small" onClick={() => open()}>
          +{t('添加')}
        </Button>
        {instance}
      </div>
      <Table
        rowKey="type"
        components={{
          body: {
            wrapper: Container,
            row: DraggableBodyRow,
          },
        }}
        scroll={{ y: '60vh' }}
        columns={columns}
        dataSource={typeSource}
        pagination={false}
      />
    </>
  );
};

export const TicketSetting = (props: TicketSettingProps) => {
  const { storeId, taskId, typeSource, onRefresh } = props;
  const [visible, setVisible] = useState(false);
  const { t } = useTranslate();

  return (
    <>
      <SettingOutlined onClick={() => setVisible(true)} />
      <Modal
        centered
        title={t('nav.setting')}
        visible={visible}
        onCancel={() => setVisible(false)}
        bodyStyle={{ padding: 16 }}
        footer={null}
      >
        {visible && (
          <ModalContent
            storeId={storeId}
            taskId={taskId}
            typeSource={typeSource}
            onRefresh={onRefresh}
          />
        )}
      </Modal>
    </>
  );
};
